// Çağrı ekranı ve çağrı-içi kontroller (9.4.11).
//
// Sınır: sistem düzeyi çağrı arayüzü (CallKit / ConnectionService) ve arka
// planda süreklilik 9.4.6'nın işi; burası **uygulama içi** ekran. İlk dilim
// ön planda çalışıyor (9.4.13), yani bu ekran açıkken uygulama önde.
//
// Ekranın taşıdığı üç bilgi para tarafından geliyor: dakika ücreti (9.4.1),
// akan tutar (9.4.2.2) ve kalan süre (9.4.3.1). Kalan süre sunucudan okunuyor;
// buradaki sayaç yalnızca durum yoklamaları arasını yumuşatıyor.
import React, { useMemo } from 'react';
import Ionicons from '@expo/vector-icons/Ionicons';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActionSheetIOS,
  Alert,
  Linking,
  Platform,
  ScrollView,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useWindowDimensions } from 'react-native';
import Avatar from '../components/Avatar';
import { spacing, fontSize, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { formatMoney, formatPerMinute } from '../data/currencies';
import { mediaAvailable } from '../calls/media';
import { callFault } from '../calls/callMessage';
import type { CallState } from '../calls/useCall';
import type { AudioRoute } from '../calls/audioRoute';
import CallVideo from '../components/CallVideo';

type Props = CallState & {
  peerName: string;
  peerPhoto?: string | null;
  onAccept: () => void;
  onDecline: () => void;
  onHangUp: () => void;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  onSwitchCamera: () => void;
  onSelectAudioRoute: (route: AudioRoute) => void;
  onClose: () => void;
};

// Çağrı yüzeyi her temada koyudur; tema içindeki "white" ise değişken zemin
// rolüdür. Metin/ikon ve seçili kontrol bu sabit yüzeyin kontrastını korur.
const callSurfaceColors = {
  foreground: '#FFFFFF',
  selectedForeground: '#000000',
} as const;

const clock = (total: number) => {
  const m = Math.floor(total / 60),
    s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default function CallScreen({
  phase,
  call,
  incoming,
  elapsed,
  remaining,
  endingSoon,
  muted,
  audioRoute,
  audioRoutes,
  audioRoutingAvailable,
  audioRouteError,
  bluetoothPermissionDenied,
  media,
  video,
  streams,
  cameraOff,
  summary,
  error,
  errorTranslationKey,
  peerName,
  peerPhoto,
  onAccept,
  onDecline,
  onHangUp,
  onToggleMute,
  onToggleCamera,
  onSwitchCamera,
  onSelectAudioRoute,
  onClose,
}: Props) {
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const insets = useSafeAreaInsets();
  // D-419 — çağrı ekranı metin ölçeğine uyarlanmıyordu. Üst blok sabit 112 px
  // avatar, 40 pt sayaç ve en çok sekiz satır metin taşıyor; büyük yazı
  // ayarında ya da kısa ekranda içerik büyüyüp eylem satırını aşağı itiyordu.
  // Kapatma düğmesinin ulaşılamaz olması, dakika ücreti işlerken en kötü
  // sonuçtur: kullanıcı ödemeyi durduramaz.
  const { height, fontScale } = useWindowDimensions();
  const usableHeight = (height - insets.top - insets.bottom) / Math.max(fontScale, 1);
  const compact = usableHeight < 620;
  const avatarSize = compact ? 72 : 112;
  const styles = useMemo(() => makeStyles(colors, Platform.OS), [colors]);
  const actionIconSize = Platform.OS === 'ios' ? 30 : 28;
  // D-420 — arızalar üst üste binmez; tek yuvada en öncelikli olan gösterilir.
  const fault = callFault({ error, errorTranslationKey, bluetoothPermissionDenied, audioRouteError });

  const listedPricePerMin = Number(
    call?.price_per_min ?? incoming?.price_per_min ?? 0,
  );
  const pricePerMin = Number(
    call?.viewer_rate_per_min
      ?? incoming?.viewer_rate_per_min
      ?? listedPricePerMin,
  );
  const currency = call?.viewer_currency
    ?? incoming?.viewer_currency
    ?? call?.price_currency
    ?? incoming?.price_currency
    ?? null;
  const viewerAmountKind = call?.viewer_amount_kind ?? incoming?.viewer_amount_kind;
  const amountKey = viewerAmountKind === 'earned' ? 'call.earned' : 'call.spent';
  // Akan tutar: geçen saniye × dakika ücreti / 60. Sunucudaki hesapla aynı kural
  // (9.4.2.2), burada yalnızca gösterim — kesin tutar kapanışta sunucudan gelir.
  const running = (pricePerMin * elapsed) / 60;
  const routeLabels: Record<AudioRoute, string> = {
    earpiece: t('call.earpiece'),
    speaker: t('call.speaker'),
    wired: t('call.wiredHeadset'),
    bluetooth: t('call.bluetooth'),
  };
  const nextAudioRoute = (() => {
    if (!audioRoute || audioRoutes.length < 2) return null;
    const index = audioRoutes.indexOf(audioRoute);
    return audioRoutes[(index + 1) % audioRoutes.length] ?? null;
  })();
  const changeAudioRoute = () => {
    if (!nextAudioRoute) return;
    const hasAccessory = audioRoutes.some(
      route => route === 'bluetooth' || route === 'wired',
    );
    if (!hasAccessory) {
      onSelectAudioRoute(nextAudioRoute);
      return;
    }
    if (Platform.OS === 'ios') {
      const options = [
        ...audioRoutes.map(route => routeLabels[route]),
        t('common.cancel'),
      ];
      ActionSheetIOS.showActionSheetWithOptions(
        { options, cancelButtonIndex: options.length - 1, title: t('call.audioOutputTitle') },
        index => {
          const route = audioRoutes[index];
          if (route) onSelectAudioRoute(route);
        },
      );
      return;
    }
    Alert.alert(
      t('call.audioOutputTitle'),
      undefined,
      [
        ...audioRoutes.map(route => ({
          text: routeLabels[route],
          onPress: () => onSelectAudioRoute(route),
        })),
        { text: t('common.cancel'), style: 'cancel' as const },
      ],
    );
  };
  const routeIcon = audioRoute === 'bluetooth'
    ? 'bluetooth'
    : audioRoute === 'speaker'
    ? 'volume-high'
    : audioRoute === 'wired'
    ? 'headset'
    : 'volume-medium';

  // Kopukluk sürerken "bağlandı" yazmak yanlış: kullanıcı sessizliği duyuyor ve
  // ücret işlemeye devam ediyor (9.4.4.5). Durum metni bunu söylemeli.
  const kopuk =
    phase === 'active' && (media === 'interrupted' || media === 'failed');
  const baglaniyor = phase === 'active' && media === 'connecting';
  const status =
    phase === 'incoming'
      ? t('call.incoming')
      : phase === 'outgoing'
      ? t('call.calling')
      : kopuk
      ? t('call.reconnecting')
      : baglaniyor
      ? t('call.connecting')
      : phase === 'active'
      ? t('call.connected')
      : t('call.ended');

  // 9.4.11 / D-605 düzeltmesi — üç sütunlu ızgara yalnız GÖRÜNTÜLÜ çağrının beş
  // kontrolü içindir; orada hücreler sabit üçte bir olup üçten fazlası alt satıra
  // sarar. Sesli çağrı, gelen çağrı ve bitiş ekranında ise en çok üç kontrol var:
  // orada her hücre `flex:1` ile satırı eşit paylaşır ve `minWidth:0` uzun
  // etiketlerin (ör. Fransızca) sabit dar bir sütuna sıkışıp kırpılmasını önler.
  const columnStyle = video && phase === 'active' ? styles.controlStackGrid : styles.controlStack;

  return (
    <View
      style={[
        styles.root,
        {
          paddingTop: insets.top + spacing.xl,
          paddingBottom: insets.bottom + spacing.xl,
        },
      ]}
    >
      {/* 9.4.14 — görüntülü çağrıda karşı taraf arkada tam ekran, kendi
          görüntün köşede küçük bir kutuda. Akış gelmeden hiçbir şey çizilmez,
          o yüzden bağlanma anında altındaki sesli düzen görünür kalır.
          Faz kontrolü şart: çağrı bitince kamera bırakılıyor ama akışın SON
          KARESİ referansta kalıyordu ve "çağrı sona erdi" ekranında donmuş bir
          görüntü olarak asılı duruyordu (D-596). Görüntü yalnız çağrı canlıyken
          çizilir; eylem satırıyla aynı faz kapısı. */}
      {video && (phase === 'outgoing' || phase === 'active') && (
        <>
          <CallVideo
            testID="call-remote-video"
            stream={streams.remote}
            style={styles.remoteVideo}
          />
          <CallVideo
            testID="call-local-video"
            stream={streams.local}
            style={styles.localVideo}
            mirror
          />
        </>
      )}
      {/* Üst blok kaydırılabilir ve daralabilir; eylem satırı kökün altında
          sabit kalır. Böylece metin ne kadar büyürse büyüsün kapatma düğmesi
          ekranda kalır. */}
      <ScrollView
        style={styles.topScroll}
        contentContainerStyle={styles.top}
        showsVerticalScrollIndicator={false}
        bounces={false}>
        <Text style={styles.status}>{status}</Text>
        <Avatar
          photoUrl={peerPhoto}
          name={peerName}
          size={avatarSize}
          style={styles.avatar}
        />
        <Text style={styles.name}>{peerName}</Text>

        {pricePerMin > 0 && (
          <Text style={styles.price}>
            {formatPerMinute(pricePerMin, currency, lang, t('calendar.minutesShort'))}
          </Text>
        )}

        {phase === 'active' && (
          <>
            <Text style={styles.timer}>{clock(elapsed)}</Text>
            {pricePerMin > 0 && (
              <Text style={styles.running} testID="call-running-amount">
                {t(amountKey, { amount: formatMoney(running, currency, lang) })}
              </Text>
            )}
            {remaining !== null && remaining <= 60 && (
              <Text style={styles.warning}>
                {t('call.remaining', { time: clock(remaining) })}
              </Text>
            )}
            {remaining === null && endingSoon && (
              <Text style={styles.warning}>{t('call.endingSoon')}</Text>
            )}
          </>
        )}

        {phase === 'outgoing' && (
          <ActivityIndicator style={styles.spinner} color={colors.gray400} />
        )}

        {summary && (
          <Text style={styles.summary}>
            {clock(summary.seconds)} ·{' '}
            {t(summary.amountKind === 'earned' ? 'call.earned' : 'call.spent', {
              amount: formatMoney(summary.amount, summary.currency, lang),
            })}
          </Text>
        )}
        {/* D-418 — izin reddi "başarısız" değildir: kullanıcının düzeltebileceği
            bir durumdur ve nereden düzelteceği söylenir.
            D-420 — dört arıza tek yuvayı paylaşır; üst üste binmezler. */}
        {fault?.kind === 'micPermission' ? (
          Platform.OS === 'web' ? (
            <Text style={styles.error} testID="call-fault">{t('call.micPermissionWeb')}</Text>
          ) : (
            <Text
              style={[styles.error, styles.errorAction]}
              accessibilityRole="button"
              testID="call-fault"
              onPress={() => Linking.openSettings().catch(() => {})}>
              {t('call.micPermission')}
            </Text>
          )
        ) : fault?.kind === 'bluetoothPermission' ? (
          <Text
            style={[styles.error, styles.errorAction]}
            accessibilityRole="button"
            testID="call-fault"
            onPress={() => Linking.openSettings().catch(() => {})}>
            {t('call.bluetoothPermissionDenied')}
          </Text>
        ) : fault ? (
          <Text style={styles.error} testID="call-fault">
            {fault.kind === 'error'
              ? fault.text
              : fault.kind === 'errorKey'
              ? t(fault.translationKey)
              : t('call.audioRouteFailed')}
          </Text>
        ) : null}

        {/* Ses taşınmadığı sürece bunu saklamak dürüst olmaz (9.4.4.3). Bu not
            arıza yuvasına GİRMEZ: geçici bir olay değil, yapının sürekli
            durumudur ve bir arızanın arkasına saklanması taahhüdü bozardı. */}
        {!mediaAvailable && phase !== 'ended' && (
          <Text style={styles.notice}>{t('call.noAudioNotice')}</Text>
        )}
      </ScrollView>

      <View style={styles.actions}>
        {phase === 'incoming' && (
          <>
            <View style={columnStyle}>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={t('call.decline')}
                testID="call-action-decline"
                style={[styles.circleButton, styles.decline]}
                onPress={onDecline}
              >
                <Ionicons
                  name="call"
                  size={actionIconSize}
                  color={callSurfaceColors.foreground}
                  style={styles.hangUpIcon}
                />
              </TouchableOpacity>
              <Text style={styles.actionLabel}>{t('call.decline')}</Text>
            </View>
            <View style={columnStyle}>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={t('call.accept')}
                testID="call-action-accept"
                style={[styles.circleButton, styles.accept]}
                onPress={onAccept}
              >
                <Ionicons
                  name="call"
                  size={actionIconSize}
                  color={callSurfaceColors.foreground}
                />
              </TouchableOpacity>
              <Text style={styles.actionLabel}>{t('call.accept')}</Text>
            </View>
          </>
        )}

        {(phase === 'outgoing' || phase === 'active') && (
          <>
            {phase === 'active' && (
              <>
                {audioRoutingAvailable && audioRoute && nextAudioRoute && (
                  <View style={columnStyle}>
                    <TouchableOpacity
                      accessibilityRole="button"
                      accessibilityLabel={t('call.audioOutput', {
                        route: routeLabels[audioRoute],
                      })}
                      testID="call-action-audio-route"
                      style={[
                        styles.circleButton,
                        styles.neutral,
                        audioRoute !== 'earpiece' && styles.neutralSelected,
                      ]}
                      onPress={changeAudioRoute}
                    >
                      <Ionicons
                        name={routeIcon}
                        size={actionIconSize}
                        color={audioRoute !== 'earpiece' ? callSurfaceColors.selectedForeground : callSurfaceColors.foreground}
                      />
                    </TouchableOpacity>
                    <Text style={styles.actionLabel}>{routeLabels[audioRoute]}</Text>
                  </View>
                )}
                <View style={columnStyle}>
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={muted ? t('call.unmute') : t('call.mute')}
                    accessibilityState={{ selected: muted }}
                    testID="call-action-mute"
                    style={[
                      styles.circleButton,
                      styles.neutral,
                      muted && styles.neutralSelected,
                    ]}
                    onPress={onToggleMute}
                  >
                    <Ionicons
                      name={muted ? 'mic-off' : 'mic'}
                      size={actionIconSize}
                      color={muted ? callSurfaceColors.selectedForeground : callSurfaceColors.foreground}
                    />
                  </TouchableOpacity>
                  <Text style={styles.actionLabel}>
                    {muted ? t('call.unmute') : t('call.mute')}
                  </Text>
                </View>
                {/* 9.4.14 — kamera kontrolleri yalnız görüntülü çağrıda çizilir.
                    Sesli çağrıda kamera hiç istenmediği için kapatacak bir şey
                    de yok; düğmeyi göstermek çalışmayan bir söz olurdu. */}
                {video && (
                  <>
                    <View style={columnStyle}>
                      <TouchableOpacity
                        accessibilityRole="button"
                        accessibilityLabel={cameraOff ? t('call.cameraOn') : t('call.cameraOff')}
                        accessibilityState={{ selected: cameraOff }}
                        testID="call-action-camera"
                        style={[styles.circleButton, styles.neutral, cameraOff && styles.neutralSelected]}
                        onPress={onToggleCamera}
                      >
                        <Ionicons
                          name={cameraOff ? 'videocam-off' : 'videocam'}
                          size={actionIconSize}
                          color={cameraOff ? callSurfaceColors.selectedForeground : callSurfaceColors.foreground}
                        />
                      </TouchableOpacity>
                      <Text style={styles.actionLabel}>
                        {cameraOff ? t('call.cameraOn') : t('call.cameraOff')}
                      </Text>
                    </View>
                    <View style={columnStyle}>
                      <TouchableOpacity
                        accessibilityRole="button"
                        accessibilityLabel={t('call.flipCamera')}
                        testID="call-action-flip-camera"
                        style={[styles.circleButton, styles.neutral]}
                        onPress={onSwitchCamera}
                        disabled={cameraOff}
                      >
                        <Ionicons
                          name="camera-reverse"
                          size={actionIconSize}
                          color={callSurfaceColors.foreground}
                        />
                      </TouchableOpacity>
                      <Text style={styles.actionLabel}>{t('call.flipCamera')}</Text>
                    </View>
                  </>
                )}
              </>
            )}
            <View style={columnStyle}>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={t('call.hangUp')}
                testID="call-action-hang-up"
                style={[styles.circleButton, styles.decline]}
                onPress={onHangUp}
              >
                <Ionicons
                  name="call"
                  size={actionIconSize}
                  color={callSurfaceColors.foreground}
                  style={styles.hangUpIcon}
                />
              </TouchableOpacity>
              <Text style={styles.actionLabel}>{t('call.hangUp')}</Text>
            </View>
          </>
        )}

        {phase === 'ended' && (
          <View style={columnStyle}>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={t('call.close')}
              testID="call-action-close"
              style={[styles.circleButton, styles.neutral]}
              onPress={onClose}
            >
              <Ionicons
                name="close"
                size={actionIconSize}
                color={callSurfaceColors.foreground}
              />
            </TouchableOpacity>
            <Text style={styles.actionLabel}>{t('call.close')}</Text>
          </View>
        )}
      </View>
    </View>
  );
}

const makeStyles = (colors: ThemeColors, platform: string) => {
  const ios = platform === 'ios';
  const circleSize = ios ? 72 : 64;
  return StyleSheet.create({
    root: {
      flex: 1,
      backgroundColor: ios ? '#19191B' : '#101114',
      paddingHorizontal: ios ? spacing.xl : spacing.lg,
      justifyContent: 'space-between',
    },
    // Uzak görüntü kökü kaplar ve içeriğin ARKASINDA durur: metin ve düğmeler
    // üstünde okunur kalsın. Kendi görüntün sağ üstte, güvenli alanın altında.
    // Kök yatay boşluk taşıyor ve mutlak konum o boşluğun İÇİNE hizalanır;
    // sol/sağ negatifi olmadan görüntü tam ekran olmaz, kenarlarda şerit kalır.
    remoteVideo: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: -(ios ? spacing.xl : spacing.lg),
      right: -(ios ? spacing.xl : spacing.lg),
      backgroundColor: '#000000',
    },
    localVideo: {
      position: 'absolute',
      right: spacing.lg,
      top: spacing.xl * 2,
      width: 96,
      height: 128,
      borderRadius: radius.card,
      overflow: 'hidden',
      backgroundColor: '#000000',
    },
    topScroll: { flex: 1, width: '100%' },
    top: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      flexGrow: 1,
      paddingBottom: spacing.md,
    },
    status: {
      fontSize: ios ? fontSize.sm : 14,
      color: colors.gray400,
      letterSpacing: ios ? 0.4 : 0,
    },
    avatar: { marginTop: ios ? spacing.xl : spacing.lg },
    name: {
      fontSize: ios ? 28 : 26,
      fontWeight: ios ? '500' : '600',
      color: callSurfaceColors.foreground,
      marginTop: spacing.md,
    },
    price: { fontSize: fontSize.sm, color: colors.gray400 },
    timer: {
      fontSize: 40,
      fontWeight: '300',
      color: callSurfaceColors.foreground,
      marginTop: spacing.lg,
      fontVariant: ['tabular-nums'],
    },
    // Sayaçla birlikte her saniye güncellenir; orantılı rakamlarla genişliği
    // değişip titrerdi. Sayaç ve geçmiş ekranı zaten sabit genişlik kullanıyor.
    running: { fontSize: fontSize.md, color: colors.gray400, fontVariant: ['tabular-nums'] },
    warning: { fontSize: fontSize.sm, color: '#E4A11B', marginTop: spacing.xs },
    spinner: { marginTop: spacing.lg },
    summary: {
      fontSize: fontSize.md,
      color: colors.gray400,
      marginTop: spacing.lg,
    },
    errorAction: { textDecorationLine: 'underline' },
    error: {
      fontSize: fontSize.sm,
      color: '#E4572E',
      marginTop: spacing.md,
      textAlign: 'center',
    },
    notice: {
      fontSize: 11,
      color: colors.gray400,
      marginTop: spacing.lg,
      textAlign: 'center',
      opacity: 0.8,
    },
    // 9.4.11 — kontroller iPhone'daki gibi üç sütunlu bir ızgara: fazlası alt
    // satıra kayar. Görüntülü çağrıda beş kontrol (ses yolu, sessiz, kamera,
    // çevir, kapat) tek satırda yan yana sıkışıyordu; üstte üç, altta iki
    // olarak sarınca hem tanıdık hem de metin etiketleri okunur kalıyor.
    actions: {
      width: '100%',
      minHeight: ios ? 112 : 104,
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      justifyContent: 'center',
      rowGap: spacing.lg,
    },
    // Sesli/gelen/bitiş satırı: en çok üç kontrol, her biri `flex:1` ile satırı
    // eşit paylaşır. `minWidth:0` olmadan uzun bir etiket kendi sütununa taban
    // genişlik dayatıp diğerlerini iterdi.
    controlStack: { flex: 1, minWidth: 0, alignItems: 'center', gap: spacing.sm },
    // Görüntülü çağrı ızgarası: her hücre satırın tam üçte biri; üç kontrol bir
    // satırı doldurur, dördüncü ve beşinci alt satıra sarar ve orta hizada durur.
    controlStackGrid: { width: '33.333%', alignItems: 'center', gap: spacing.sm },
    circleButton: {
      width: circleSize,
      height: circleSize,
      borderRadius: circleSize / 2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    hangUpIcon: { transform: [{ rotate: '135deg' }] },
    accept: { backgroundColor: '#2E9E5B' },
    decline: { backgroundColor: ios ? '#FF3B30' : '#D93025' },
    neutral: { backgroundColor: ios ? 'rgba(255,255,255,0.16)' : '#303136' },
    neutralSelected: { backgroundColor: callSurfaceColors.foreground },
    actionLabel: {
      color: callSurfaceColors.foreground,
      fontSize: fontSize.sm,
      alignSelf: 'stretch',
      textAlign: 'center',
    },
  });
};
